/**
 * DashboardDateRangeControl
 *
 * A toolbar control that lets the user set a single date range that fans out
 * to every participating panel on the dashboard.
 * Only rendered when ≥1 panel on the dashboard participates in the date range.

 *  - Relative presets + custom fixed-date option.
 *  - Explicit Apply button — panels don't react until Apply is clicked.
 *  - Remembered checkbox "Also update panels with custom ranges".
 *  - Single-date panels receive the range's end date.
 */

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import {useState} from 'react';

import {DashboardDateRange} from '../../interfaces/Dashboard';
import {
  DATE_RANGE_PRESET_LABELS,
  DateRangePreset,
  resolveDateRangePreset,
} from '../../utils/dateRange';
import {DateRangePresetSelect} from '../selectors/DateRangePresetSelect';
import {DateRangeSelect} from '../selectors/DateRangeSelect';

interface Props {
  /** Current persisted value — loaded from data.dateRange. */
  value: DashboardDateRange | undefined;
  /** Called when the user clicks Apply with a new range. */
  onApply: (
    range: DashboardDateRange,
    resolved: {date_start: string; date_end: string}
  ) => void;
  /**
   * When true the full Chip+Popover control is shown (dashboard EDIT users).
   * When false (default) a read-only chip is shown — non-owners see the range
   * but cannot change it.
   */
  editable?: boolean;
}

export const DashboardDateRangeControl = ({
  value,
  onApply,
  editable = false,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Local (uncommitted) state while the popover is open
  const [preset, setPreset] = useState<DateRangePreset>(
    value?.preset ?? 'last_7_days'
  );
  const [customStart, setCustomStart] = useState<Date | null>(
    value?.date_start ? dayjs(value.date_start).toDate() : null
  );
  const [customEnd, setCustomEnd] = useState<Date | null>(
    value?.date_end ? dayjs(value.date_end).toDate() : null
  );
  const [applyToOverridden, setApplyToOverridden] = useState<boolean>(
    value?.applyToOverridden ?? false
  );

  const isCustom = preset === 'custom';

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    // Re-sync with the persisted value each time the popover opens
    setPreset(value?.preset ?? 'last_7_days');
    setCustomStart(value?.date_start ? dayjs(value.date_start).toDate() : null);
    setCustomEnd(value?.date_end ? dayjs(value.date_end).toDate() : null);
    setApplyToOverridden(value?.applyToOverridden ?? false);
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const isApplyDisabled = isCustom && (!customStart || !customEnd);

  const handleApply = () => {
    const date_start = isCustom
      ? customStart
        ? dayjs(customStart).format('YYYY-MM-DD')
        : null
      : null;
    const date_end = isCustom
      ? customEnd
        ? dayjs(customEnd).format('YYYY-MM-DD')
        : null
      : null;

    const range: DashboardDateRange = {
      preset,
      date_start,
      date_end,
      applyToOverridden,
    };

    const resolved = resolveDateRangePreset(preset, date_start, date_end);
    onApply(range, resolved);
    handleClose();
  };

  // Chip label: show the active preset label, or date range for custom
  const chipLabel = value
    ? value.preset === 'custom'
      ? `${value.date_start ?? '?'} → ${value.date_end ?? '?'}`
      : DATE_RANGE_PRESET_LABELS[value.preset] ?? value.preset
    : 'Set Dashboard Date';

  const open = Boolean(anchorEl);

  // Non-editors see a read-only chip — they can't change the range.
  if (!editable) {
    if (!value) {
      return null;
    }
    return (
      <Tooltip title="Dashboard date range (set by the dashboard owner)">
        <Chip
          icon={<CalendarMonthIcon fontSize="small" />}
          label={chipLabel}
          variant="filled"
          color="primary"
          size="small"
          sx={{fontWeight: 600, cursor: 'default'}}
        />
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip title="Set a date range that applies to all panels on this dashboard">
        <Chip
          icon={<CalendarMonthIcon fontSize="small" />}
          label={chipLabel}
          onClick={handleOpen}
          variant={value ? 'filled' : 'outlined'}
          color={value ? 'primary' : 'default'}
          size="small"
          sx={{cursor: 'pointer', fontWeight: value ? 600 : 400}}
        />
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        PaperProps={{sx: {p: 2, width: 340}}}
      >
        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
          Dashboard Date Range
        </Typography>

        <DateRangePresetSelect value={preset} onChange={setPreset} fullWidth />

        {isCustom && (
          <Box mt={1.5}>
            <DateRangeSelect
              labels={['Start date', 'End date']}
              value={[customStart, customEnd]}
              size="small"
              maxDate={dayjs().toDate()}
              onChange={([start, end]) => {
                setCustomStart(start);
                setCustomEnd(end);
              }}
            />
          </Box>
        )}

        <FormControlLabel
          sx={{mt: 1.5, display: 'flex'}}
          control={
            <Checkbox
              size="small"
              checked={applyToOverridden}
              onChange={(e) => setApplyToOverridden(e.target.checked)}
            />
          }
          label={
            <Typography variant="body2">
              Also update panels with custom ranges
            </Typography>
          }
        />

        <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
          <Button size="small" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleApply}
            disabled={isApplyDisabled}
          >
            Apply
          </Button>
        </Box>
      </Popover>
    </>
  );
};
